const {Component} = require("react");
const PropTypes = require("prop-types");
const {classList} = require("helpers/html");
const Date = require("components/forms/Date");
const Select = require("components/forms/Select");
const {toSameDayOfWeek, copyTime, toSameWeek} = require("helpers/moment");
const moment = require("moment");
const env = require("services/env");
const assign = require("object-assign");
const {cloneObject} = require("helpers/object");
const {repeaterTo} = require("nevermorejs/lib/item");
const router = require("services/router");

class SlotEditorForm extends Component {

	static get propTypes() {
		return {
			item: PropTypes.any,
			minDate: PropTypes.any,
			handleSave: PropTypes.func,
			handleDelete: PropTypes.func,
			handleAddException: PropTypes.func,
			handleCancel: PropTypes.func,
			getTimeIntervals: PropTypes.func,
			canRepeat: PropTypes.func,
			isExclusionRemovable: PropTypes.func,
			getDate: PropTypes.func
		};
	}

	constructor(props) {
		super(props);
		this.state = {item: props.item, changes: {}};
	}

	componentWillReceiveProps(props) {
		if (props.item) {
			this.setState({item: props.item, changes: {}});
		}
	}

	get hasItem() {
		return this.state.item != undefined;
	}

	get date() {
		return typeof this.props.getDate === "function" ?
			this.props.getDate() : moment();
	}

	get rawFrom() {
		return (this.state.changes.from || this.state.item.from);
	}

	get rawTo() {
		return (this.state.changes.to || this.state.item.to);
	}

	get from() {
		if (!this.hasItem) {
			return;
		}

		const from = this.rawFrom;
		return this.repeats ? toSameWeek(from, this.date) : from;
	}

	get fromTime() {
		return this.hasItem ? this.from.format("HH:mm") : "";
	}

	get to() {
		if (!this.hasItem) {
			return;
		}

		const to = this.rawTo;
		return this.repeats ? toSameWeek(to, this.date) : to;
	}

	get toTime() {
		return this.hasItem ? this.to.format("HH:mm") : "";
	}

	get dateText() {
		return this.hasItem ? this.from.format("dddd [d.] DD MMMM YYYY") : null;
	}

	get fromTimeText() {
		return this.hasItem ? this.from.format("HH:mm") : null;
	}

	get toTimeText() {
		return this.hasItem ? this.to.format("HH:mm") : null;
	}

	get repeater() {
		if (!this.hasItem) {
			return;
		}

		return (this.state.changes.repeater || this.state.item.repeater);
	}

	get repeats() {
		const repeater = this.repeater;
		return repeater && (typeof repeater === "object") && (repeater.state === true);
	}

	get repeaterText() {
		if (!this.hasItem) {
			return;
		}

		return this.repeats ?
			`Gentages hver ${this.from.format("dddd").toLowerCase()}` :
			`Gentages ikke`;
	}

	get data() {
		if (!this.hasItem) {
			return;
		}

		return (this.state.changes.data || this.state.item.data);
	}

	get notificationText() {
		return this.hasItem ?
			`Påmindelse: ${this.data.notify_offset} minutter` :
			null;
	}

	get dateProps() {
		return {
			min: this.props.minDate.format("YYYY-MM-DD")
		};
	}

	get fromOptions() {
		if (!this.hasItem || (typeof this.props.getTimeIntervals !== "function")) {
			return [];
		}

		return this.props.getTimeIntervals(this.state.item, this.state.changes).reduce((acc, interval) => {
			let currentTime = interval.from.clone();
			let currentTimeFormatted;

			while (currentTime.isBefore(interval.to)) {
				if (interval.to.diff(currentTime, "hour") >= 1) {
					currentTimeFormatted = currentTime.format("HH:mm");
					acc.push({value: currentTimeFormatted, label: currentTimeFormatted});
				}

				currentTime.add(1, "hour");
			}

			return acc;
		}, []);
	}

	get toOptions() {
		if (!this.hasItem || (typeof this.props.getTimeIntervals !== "function")) {
			return [];
		}

		const from = this.from;

		return this.props.getTimeIntervals(this.state.item, this.state.changes).reduce((acc, interval) => {
			let currentTime = interval.from.clone();
			let currentTimeFormatted;

			while (currentTime.isSameOrBefore(interval.to)) {
				if (currentTime.isAfter(from) && (currentTime.diff(from, "hour") >= 1)) {
					currentTimeFormatted = currentTime.format("HH:mm");
					acc.push({value: currentTimeFormatted, label: currentTimeFormatted});
				}

				currentTime.add(1, "hour");
			}

			return acc;
		}, []);
	}

	get repeaterOptions() {
		if (!this.hasItem) {
			return [];
		}

		const options = [{value: "", label: "Gentages ikke"}];

		if (typeof this.props.canRepeat === "function" && this.props.canRepeat(this.state.item, this.state.changes)) {
			options.push({
				value: "1",
				label: `Hver ${this.from.format("dddd").toLowerCase()}`
			});
		}

		return options;
	}

	get notificationOptions() {
		if (!this.hasItem) {
			return [];
		}

		return env.get("options.scheduler.notificationOptions", []).map(option => {
			return {label: `${option} minutter`, value: option};
		});
	}

	get notificationOffset() {
		if (!this.hasItem) {
			return;
		}

		return this.state.changes.data && this.state.changes.data.notify_offset ?
			this.state.changes.data.notify_offset : this.state.item.data.notify_offset;
	}

	get repeaterValue() {
		return this.repeats ? "1" : "";
	}

	onCancel = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (typeof this.props.handleCancel === "function") {
			return this.props.handleCancel(this.state.item, this.state.changes);
		}
	};

	onDelete = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (typeof this.props.handleDelete === "function") {
			return this.props.handleDelete(this.state.item, this.state.changes);
		}
	};

	onAddException = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (typeof this.props.handleAddException === "function") {
			return this.props.handleAddException(this.state.item, this.state.changes);
		}
	};

	onSave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (typeof this.props.handleSave === "function") {
			return this.props.handleSave(this.state.item, this.state.changes);
		}
	};

	updateDate = (state) => {
		const value = moment(this.dateElement.value);
		state.changes.from = copyTime(value.clone(), this.from).millisecond(0);
		state.changes.to = copyTime(value.clone(), this.to).millisecond(0);
		state.changes.repeater = repeaterTo(this.repeater, value);
		return state;
	};

	onDateChange = () => {
		this.setState(state => this.updateDate(state), () => this.update());
	};

	updateNotificationOffset = (state) => {
		const value = parseInt(this.notificationElement.value);

		state.changes.data = assign({}, this.data, {
			notify_offset: value
		});

		return state;
	};

	onNotificationOffsetChange = () => {
		this.setState(state => this.updateNotificationOffset(state), () => this.update());
	};

	updateRepeater = (state) => {
		const value = parseInt(this.repeaterElement.value);

		state.changes.repeater = this.repeater;
		state.changes.repeater.state = value === 1;

		return state;
	};

	onRepeaterChange = () => {
		this.setState(state => this.updateRepeater(state), () => this.update());
	};

	updateTimeFrom = (state) => {
		const value = moment(this.fromElement.value, "HH:mm");
		state.changes.from = copyTime(this.from.clone(), value).millisecond(0);
		return state;
	};

	onTimeFromChange = () => {
		this.setState(state => this.updateTimeFrom(state), () => this.update());
	};

	updateTimeTo = (state) => {
		const value = moment(this.toElement.value, "HH:mm");

		state.changes.to = copyTime(this.to.clone(), value).millisecond(0);

		if (state.changes.to.isSameOrBefore(this.from)) {
			state.changes.to.add(1, "day");
		}

		return state;
	};

	onTimeToChange = () => {
		this.setState(state => this.updateTimeTo(state), () => this.update());
	};

	removeRepeaterExclusionListener = (dateString) => {
		return (e) => {
			e.preventDefault();
			e.stopPropagation();

			const repeater = this.repeater;
			const index = repeater.except.indexOf(dateString);

			if (index > -1) {
				this.setState(state => {
					state.changes.repeater = cloneObject(repeater);
					state.changes.repeater.except.splice(index, 1);
					return state;
				});
			}
		}
	};

	update = () => {
		this.setState(state => {
			this.updateDate(state);
			this.updateNotificationOffset(state);
			this.updateRepeater(state);
			this.updateTimeFrom(state);
			this.updateTimeTo(state);
		});
	};

	renderHeader() {
		const item = this.state.item;

		const deleteActions = item && item.repeats ? (
				<React.Fragment>
					<button type="button" onClick={this.onAddException} className="btn outline-primary">Fjern</button>
					<button type="button" onClick={this.onDelete} className="btn outline-primary">Fjern alle</button>
				</React.Fragment>
			) : (
				<button type="button" onClick={this.onDelete} className="btn btn-primary">Fjern</button>
			);

		return (
			<div className="editor-header">
				<div className="date-bar">
					<span className="slot-date">{this.dateText}</span>
					<button type="button" onClick={this.onCancel} className="cancel-edit btn arrow-left">Tilbage til kalenderen</button>
					<img onClick={this.onCancel} className="close" src={router.url("img/icons/close.svg")} alt="Close"/>
				</div>
				<div className="slot-info">
					<div className="slot-info-item">
						<label>Resumé:</label>
						<span className="slot-hour slot-hour-from">{this.fromTimeText}</span>
						<img src={router.url('img/icons/time-between.svg')} alt="time between"/>
						<span className="slot-hour slot-hour-to">{this.toTimeText}</span>
					</div>
					<div className="slot-info-item">
						<span className="slot-repeater">{this.repeaterText}</span>
					</div>
					<div className="slot-info-item">
						<span className="slot-notification">{this.notificationText}</span>
					</div>
					{deleteActions}
					<button type="button" onClick={this.onSave} className="btn secondary">Afslut</button>
				</div>
			</div>
		);
	}

	renderRepeaterExclusions() {
		if (!this.hasItem || !this.repeats || (this.repeater.except.length === 0)) {
			return;
		}

		const exclusionsMarkup = this.repeater.except.map(exception => {
			const date = moment(exception);
			const removable = typeof this.props.isExclusionRemovable === "function" ?
				this.props.isExclusionRemovable(this.state.item, this.state.changes, date) :
				false;

			return (
				<div className="slot-repeater-exclusion" key={exception}>
					<span>{moment(exception).format("dddd [d.] DD MMMM YYYY")}</span>
					<button type="button" disabled={!removable} onClick={this.removeRepeaterExclusionListener(exception)}/>
				</div>
			);
		});

		return (
			<div className="slot-repeater-exclusions">
				<label>Gentag undtagelser</label>
				{exclusionsMarkup}
			</div>
		);
	}

	renderBody() {
		return (
			<div className="editor-body">
				<div className="slot-bounds">
					<Date ref={el => this.dateElement = el} label="Dato" {...this.dateProps}
								value={this.from ? this.from.format("YYYY-MM-DD") : null}
								handleChange={this.onDateChange}/>
					<Select inputRef={el => this.fromElement = el} label="Starttidspunkt" options={this.fromOptions}
									value={this.fromTime}
									onChange={this.onTimeFromChange}/>
					<Select inputRef={el => this.toElement = el} label="Sluttidspunkt" options={this.toOptions}
									value={this.toTime}
									onChange={this.onTimeToChange}/>
				</div>
				<div className="slot-options">
					<Select inputRef={el => this.notificationElement = el} label="Påmindelse" options={this.notificationOptions}
									value={this.notificationOffset}
									onChange={this.onNotificationOffsetChange}/>
					<Select inputRef={el => this.repeaterElement = el} label="Gentages" options={this.repeaterOptions}
									value={this.repeaterValue}
									onChange={this.onRepeaterChange}/>
				</div>
				{this.renderRepeaterExclusions()}
			</div>
		);
	}

	render() {
		return (
			<div className="calendar-slot-editor-wrap">
				<div className="calendar-slot-editor-container">
					<div className="calendar-slot-editor-overlay" onClick={this.onCancel}></div>
					<div className={classList("calendar-slot-editor", this.props.className)}>
						{this.renderHeader()}
						{this.renderBody()}
					</div>
				</div>
			</div>
		);
	}

}

module.exports = SlotEditorForm;



// WEBPACK FOOTER //
// ./resources/js/components/backend/calendar/SlotEditorForm.js